import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ClipboardList, IndianRupee, PackageCheck, Pencil, Plus, Send, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import {
  DataTable,
  KpiCard,
  Panel,
  inr,
  num,
  type DataColumn,
} from "@/components/admin/analytics/AnalyticsKit";
import { FieldGroup, NotesField, SelectField, TextField } from "@/components/admin/delivery/form-bits";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  deletePurchaseOrder,
  listInventorySuppliers,
  listPurchaseOrders,
  listStockItems,
  savePurchaseOrder,
  setPurchaseOrderStatus,
} from "@/lib/admin/inventory.functions";
import {
  PO_STATUSES,
  PO_STATUS_LABEL,
  purchaseOrderSchema,
  type PurchaseOrder,
} from "@/lib/admin/inventory.schemas";

export const Route = createFileRoute("/_admin/admin/purchase-orders")({
  head: () => ({
    meta: [
      { title: "Purchase Orders — Admin" },
      {
        name: "description",
        content:
          "Raise and track kitchen purchase orders with supplier, quantities, rates, GST and expected delivery.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: PurchaseOrdersPage,
});

type DraftLine = { item_id: string; quantity: number; rate_paise: number };

function today(): string {
  const now = new Date();
  return new Date(now.getTime() + (330 + now.getTimezoneOffset()) * 60_000)
    .toISOString()
    .slice(0, 10);
}

function PurchaseOrdersPage() {
  const queryClient = useQueryClient();
  const listFn = useServerFn(listPurchaseOrders);
  const supFn = useServerFn(listInventorySuppliers);
  const itemFn = useServerFn(listStockItems);
  const saveFn = useServerFn(savePurchaseOrder);
  const statusFn = useServerFn(setPurchaseOrderStatus);
  const deletePOFn = useServerFn(deletePurchaseOrder);

  const orders = useQuery({
    queryKey: ["admin", "inventory", "purchase-orders"],
    queryFn: () => listFn(),
    staleTime: 15_000,
  });
  const suppliers = useQuery({
    queryKey: ["admin", "inventory", "suppliers"],
    queryFn: () => supFn(),
    staleTime: 60_000,
  });
  const items = useQuery({
    queryKey: ["admin", "inventory", "items"],
    queryFn: () => itemFn(),
    staleTime: 60_000,
  });

  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState<PurchaseOrder | null>(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [supplierId, setSupplierId] = useState("");
  const [orderDate, setOrderDate] = useState(today());
  const [expected, setExpected] = useState(today());
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [editing, setEditing] = useState<PurchaseOrder | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<PurchaseOrder | null>(null);
  const [formError, setFormError] = useState("");

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["admin", "inventory"] });

  const create = useMutation({
    mutationFn: (payload: unknown) => saveFn({ data: payload as never }),
    onSuccess: async (res) => {
      toast.success(editing ? `Purchase order updated` : `Purchase order ${res.po_number} created`);
      setCreating(false);
      setEditing(null);
      setLines([]);
      setNotes("");
      setSupplierId("");
      await invalidate();
    },
    onError: () => toast.error(editing ? "Couldn't update purchase order." : "Couldn't create the purchase order."),
  });

  const changeStatus = useMutation({
    mutationFn: (v: { id: string; status: string }) => statusFn({ data: v as never }),
    onSuccess: async () => {
      toast.success("Status updated");
      await invalidate();
    },
    onError: () => toast.error("Couldn't update status."),
  });

  const removePO = useMutation({
    mutationFn: (id: string) => deletePOFn({ data: { id } }),
    onSuccess: async () => {
      toast.success("Purchase order deleted");
      setConfirmDelete(null);
      await invalidate();
    },
    onError: (e: any) => {
      const msg = e.message === "deletion_blocked_received" 
        ? "Cannot delete a received order. Delete the receipt first." 
        : "Couldn't delete purchase order.";
      toast.error(msg);
    },
  });

  const handleEdit = (po: PurchaseOrder) => {
    setEditing(po);
    setSupplierId(po.supplier_id || "");
    setOrderDate(po.order_date);
    setExpected(po.expected_delivery || "");
    setNotes(po.notes || "");
    setLines(po.lines.map(l => ({ 
      item_id: l.item_id || "", 
      quantity: l.quantity, 
      rate_paise: l.rate_paise 
    })));
    setCreating(true);
  };

  const handleNew = () => {
    setEditing(null);
    setSupplierId("");
    setOrderDate(today());
    setExpected(today());
    setNotes("");
    setLines([]);
    setCreating(true);
  };

  const all = useMemo(() => orders.data ?? [], [orders.data]);
  const rows = useMemo(
    () => (statusFilter === "all" ? all : all.filter((o) => o.status === statusFilter)),
    [all, statusFilter],
  );
  const kpi = useMemo(
    () => ({
      total: all.length,
      pending: all.filter((o) => o.status === "draft" || o.status === "sent").length,
      received: all.filter((o) => o.status === "received").length,
      value: all
        .filter((o) => o.status !== "cancelled")
        .reduce((s, o) => s + o.total_paise, 0),
    }),
    [all],
  );

  const itemMap = useMemo(
    () => new Map((items.data ?? []).map((i) => [i.id, i])),
    [items.data],
  );

  const draftTotals = useMemo(() => {
    let subtotal = 0;
    let gst = 0;
    for (const l of lines) {
      const meta = itemMap.get(l.item_id);
      const line = Math.round(l.quantity * l.rate_paise);
      subtotal += line;
      gst += Math.round((line * (meta?.gst_rate ?? 5)) / 100);
    }
    return { subtotal, gst, total: subtotal + gst };
  }, [lines, itemMap]);

  const submit = () => {
    const payload = {
      supplier_id: supplierId,
      order_date: orderDate,
      expected_delivery: expected || null,
      status: editing ? editing.status : "sent",
      notes,
      lines,
    };
    const parsed = purchaseOrderSchema.safeParse(payload);
    if (!parsed.success) {
      setFormError(parsed.error.issues[0]?.message ?? "Check the form");
      return;
    }
    setFormError("");
    create.mutate({ id: editing?.id || null, patch: parsed.data });
  };

  const columns: Array<DataColumn<PurchaseOrder>> = [
    {
      key: "po",
      header: "PO number",
      render: (r) => <span className="font-mono text-xs">{r.po_number}</span>,
      sortValue: (r) => r.po_number,
      exportValue: (r) => r.po_number,
    },
    {
      key: "supplier",
      header: "Supplier",
      render: (r) => <span className="font-medium text-foreground">{r.supplier_name}</span>,
      sortValue: (r) => r.supplier_name,
      exportValue: (r) => r.supplier_name,
    },
    {
      key: "date",
      header: "Order date",
      render: (r) => r.order_date,
      sortValue: (r) => r.order_date,
      exportValue: (r) => r.order_date,
    },
    {
      key: "expected",
      header: "Expected",
      render: (r) => r.expected_delivery ?? "—",
      sortValue: (r) => r.expected_delivery ?? "",
      exportValue: (r) => r.expected_delivery ?? "",
    },
    {
      key: "items",
      header: "Items",
      align: "right",
      render: (r) => num(r.lines.length),
      sortValue: (r) => r.lines.length,
      exportValue: (r) => r.lines.length,
    },
    {
      key: "subtotal",
      header: "Subtotal",
      align: "right",
      render: (r) => inr(r.subtotal_paise / 100),
      sortValue: (r) => r.subtotal_paise,
      exportValue: (r) => r.subtotal_paise / 100,
    },
    {
      key: "gst",
      header: "GST",
      align: "right",
      render: (r) => inr(r.gst_paise / 100),
      sortValue: (r) => r.gst_paise,
      exportValue: (r) => r.gst_paise / 100,
    },
    {
      key: "total",
      header: "Total",
      align: "right",
      render: (r) => <span className="font-medium">{inr(r.total_paise / 100)}</span>,
      sortValue: (r) => r.total_paise,
      exportValue: (r) => r.total_paise / 100,
    },
    {
      key: "grn",
      header: "GRN",
      render: (r) => <span className="font-mono text-[11px]">{r.grn_number || "—"}</span>,
      exportValue: (r) => r.grn_number,
    },
    {
      key: "status",
      header: "Status",
      render: (r) => (
        <Badge variant={r.status === "cancelled" ? "destructive" : "secondary"}>
          {PO_STATUS_LABEL[r.status] ?? r.status}
        </Badge>
      ),
      exportValue: (r) => PO_STATUS_LABEL[r.status] ?? r.status,
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      render: (r) => (
        <div className="flex justify-end gap-1">
          <Button size="sm" variant="outline" className="h-8" onClick={() => setViewing(r)}>
            View
          </Button>
          {r.status !== "received" && r.status !== "partially_received" ? (
            <Button size="sm" variant="outline" className="h-8" onClick={() => handleEdit(r)}>
              <Pencil className="h-3.5 w-3.5" aria-hidden />
            </Button>
          ) : null}
          {r.status === "draft" ? (
            <Button
              size="sm"
              variant="outline"
              className="h-8 gap-1"
              onClick={() => changeStatus.mutate({ id: r.id, status: "sent" })}
            >
              <Send className="h-3.5 w-3.5" aria-hidden />
              Send
            </Button>
          ) : null}
          {r.status !== "received" && r.status !== "cancelled" ? (
            <Button
              size="sm"
              variant="outline"
              className="h-8 gap-1 text-destructive"
              onClick={() => changeStatus.mutate({ id: r.id, status: "cancelled" })}
            >
              <X className="h-3.5 w-3.5" aria-hidden />
              Cancel
            </Button>
          ) : null}
          {r.status !== "received" && r.status !== "partially_received" ? (
            <Button
              size="sm"
              variant="outline"
              className="h-8 text-destructive"
              onClick={() => setConfirmDelete(r)}
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden />
            </Button>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div>
      <AdminPageHeader
        title="Purchase Orders"
        description="Raise indents to suppliers, track GST and totals, and follow each order through to receipt."
      />

      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
        <KpiCard
          label="Purchase orders"
          value={num(kpi.total)}
          tone="info"
          icon={ClipboardList}
          loading={orders.isPending}
        />
        <KpiCard
          label="Pending"
          value={num(kpi.pending)}
          tone="warn"
          icon={Send}
          loading={orders.isPending}
        />
        <KpiCard
          label="Received"
          value={num(kpi.received)}
          tone="success"
          icon={PackageCheck}
          loading={orders.isPending}
        />
        <KpiCard
          label="Purchase value"
          value={inr(kpi.value / 100)}
          tone="teal"
          icon={IndianRupee}
          loading={orders.isPending}
        />
      </div>

      <Panel title="All purchase orders" subtitle={`${rows.length} of ${all.length} shown`}>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant={statusFilter === "all" ? "default" : "outline"}
            onClick={() => setStatusFilter("all")}
          >
            All
          </Button>
          {PO_STATUSES.map((st) => (
            <Button
              key={st}
              size="sm"
              variant={statusFilter === st ? "default" : "outline"}
              onClick={() => setStatusFilter(st)}
            >
              {PO_STATUS_LABEL[st]}
            </Button>
          ))}
          <Button size="sm" className="ml-auto gap-1.5" onClick={handleNew}>
            <Plus className="h-3.5 w-3.5" aria-hidden />
            New purchase order
          </Button>
        </div>
        <DataTable
          rows={rows}
          columns={columns}
          loading={orders.isPending}
          exportName="purchase-orders"
          importModule="purchase-orders"
          pageSize={12}
          minWidth={1200}
          searchOn={(r) => `${r.po_number} ${r.supplier_name} ${r.grn_number}`}
        />
      </Panel>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-h-[88vh] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit PO ${editing.po_number}` : "New purchase order"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <FieldGroup title="Order details">
              <SelectField
                label="Supplier"
                value={supplierId}
                onChange={setSupplierId}
                options={(suppliers.data ?? []).map((s) => ({
                  value: s.id,
                  label: s.supplier_name,
                }))}
              />
              <TextField label="Order date" type="date" value={orderDate} onChange={setOrderDate} />
              <TextField
                label="Expected delivery"
                type="date"
                value={expected}
                onChange={setExpected}
              />
              <NotesField value={notes} onChange={setNotes} />
            </FieldGroup>

            <section className="rounded-lg border border-border bg-muted/20 p-3">
              <header className="mb-3 flex items-center justify-between">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Items
                </h3>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5"
                  onClick={() =>
                    setLines([...lines, { item_id: "", quantity: 1, rate_paise: 0 }])
                  }
                >
                  <Plus className="h-3.5 w-3.5" aria-hidden />
                  Add line
                </Button>
              </header>
              {lines.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  No items yet — add at least one line to raise this order.
                </p>
              ) : (
                <div className="space-y-2">
                  {lines.map((l, idx) => {
                    const meta = itemMap.get(l.item_id);
                    return (
                      <div
                        key={idx}
                        className="grid items-end gap-2 rounded-md border border-border bg-background p-2 sm:grid-cols-[2fr_1fr_1fr_auto]"
                      >
                        <SelectField
                          label="Ingredient"
                          value={l.item_id}
                          onChange={(v) => {
                            const picked = itemMap.get(v);
                            const next = [...lines];
                            next[idx] = {
                              ...l,
                              item_id: v,
                              rate_paise: picked?.purchase_price_paise ?? l.rate_paise,
                            };
                            setLines(next);
                          }}
                          options={(items.data ?? []).map((i) => ({
                            value: i.id,
                            label: `${i.item_name} (${i.unit})`,
                          }))}
                        />
                        <div>
                          <Label className="text-xs">Quantity {meta ? `(${meta.unit})` : ""}</Label>
                          <Input
                            type="number"
                            step={0.01}
                            className="mt-1 h-9"
                            value={l.quantity}
                            onChange={(e) => {
                              const next = [...lines];
                              next[idx] = { ...l, quantity: Number(e.target.value) };
                              setLines(next);
                            }}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Rate (₹)</Label>
                          <Input
                            type="number"
                            step={0.01}
                            className="mt-1 h-9"
                            value={l.rate_paise / 100}
                            onChange={(e) => {
                              const next = [...lines];
                              next[idx] = {
                                ...l,
                                rate_paise: Math.round(Number(e.target.value) * 100),
                              };
                              setLines(next);
                            }}
                          />
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-9 text-destructive"
                          onClick={() => setLines(lines.filter((_, i) => i !== idx))}
                        >
                          <Trash2 className="h-3.5 w-3.5" aria-hidden />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="mt-3 flex flex-wrap justify-end gap-4 text-sm">
                <span className="text-muted-foreground">
                  Subtotal <strong className="text-foreground">{inr(draftTotals.subtotal / 100)}</strong>
                </span>
                <span className="text-muted-foreground">
                  GST <strong className="text-foreground">{inr(draftTotals.gst / 100)}</strong>
                </span>
                <span className="text-muted-foreground">
                  Total <strong className="text-foreground">{inr(draftTotals.total / 100)}</strong>
                </span>
              </div>
            </section>
            {formError ? <p className="text-xs text-destructive">{formError}</p> : null}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={create.isPending}>
              {create.isPending ? "Saving…" : editing ? "Save changes" : "Create purchase order"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Sheet open={viewing !== null} onOpenChange={(o) => (o ? null : setViewing(null))}>
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-2xl">
          <SheetHeader>
            <SheetTitle>{viewing?.po_number}</SheetTitle>
            <SheetDescription>
              {viewing?.supplier_name} · {viewing?.order_date}
            </SheetDescription>
          </SheetHeader>
          {viewing ? (
            <div className="mt-4 space-y-4 px-4 pb-6">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground">Status</p>
                  <p>{PO_STATUS_LABEL[viewing.status] ?? viewing.status}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Expected delivery</p>
                  <p>{viewing.expected_delivery ?? "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Received on</p>
                  <p>{viewing.received_at ?? "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">GRN</p>
                  <p className="font-mono text-xs">{viewing.grn_number || "—"}</p>
                </div>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-[11px] uppercase text-muted-foreground">
                    <th className="py-2">Item</th>
                    <th className="py-2 text-right">Qty</th>
                    <th className="py-2 text-right">Received</th>
                    <th className="py-2 text-right">Rate</th>
                    <th className="py-2 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {viewing.lines.map((l) => (
                    <tr key={l.id} className="border-b border-border/60">
                      <td className="py-2">{l.item_name}</td>
                      <td className="py-2 text-right tabular-nums">
                        {l.quantity} {l.unit}
                      </td>
                      <td className="py-2 text-right tabular-nums">{l.received_quantity}</td>
                      <td className="py-2 text-right tabular-nums">{inr(l.rate_paise / 100)}</td>
                      <td className="py-2 text-right tabular-nums">
                        {inr(l.line_total_paise / 100)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex justify-end gap-4 text-sm">
                <span className="text-muted-foreground">
                  Subtotal <strong className="text-foreground">{inr(viewing.subtotal_paise / 100)}</strong>
                </span>
                <span className="text-muted-foreground">
                  GST <strong className="text-foreground">{inr(viewing.gst_paise / 100)}</strong>
                </span>
                <span className="text-muted-foreground">
                  Total <strong className="text-foreground">{inr(viewing.total_paise / 100)}</strong>
                </span>
              </div>
              {viewing.notes ? (
                <p className="rounded-md border border-border bg-muted/30 p-2 text-xs text-muted-foreground">
                  {viewing.notes}
                </p>
              ) : null}
            </div>
          ) : null}
        </SheetContent>
      </Sheet>

      <AlertDialog
        open={confirmDelete !== null}
        onOpenChange={(o) => (o ? null : setConfirmDelete(null))}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {confirmDelete?.po_number}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the purchase order and its lines.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => confirmDelete && removePO.mutate(confirmDelete.id)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
